import { getElement } from '@stencil/core';
import { EXPERIMENT_RESULTS_GLOBAL_KEY, AMPLITUDE_READY_EVENT } from './marketing/amplitude-constants';
import { ATTRIBUTION_READY_EVENT } from './attribution/AttributionService.constants';

const ICONS_URL = `${window.location.origin}/wp-content/themes/housecallpro/dist/images/menu-icons/menu-icons.svg`;

/**
 * Executes a callback function when the document is fully loaded and ready.
 *
 * This function checks the current state of the document. If the document is already
 * loaded, it immediately executes the callback. Otherwise, it sets up an event listener
 * to execute the callback once the 'DOMContentLoaded' event fires, indicating that the
 * document is ready.
 *
 * @param {Function} callback - The function to be executed when the document is ready.
 */
const runOnDocumentReady = (callback) => {
	if (document.readyState !== 'loading') {
		callback();
	} else {
		const handler = () => {
			callback();
			document.removeEventListener('DOMContentLoaded', handler);
		};

		document.addEventListener('DOMContentLoaded', handler);
	}
};

/**
 * Executes a callback function once the A/B tests have been resolved and the 'hcpExperimentsLoaded' event has been dispatched.
 *
 * This function checks if the global flag `window.hcpExperimentsLoaded` is set to true, indicating that the A/B tests have been resolved.
 * If the flag is true, it immediately executes the provided callback function.
 * If the flag is false, it adds an event listener for the 'hcpExperimentsLoaded' event and executes the callback function once the event is dispatched.
 *
 * @param {Function} callback - The function to be executed once the A/B tests are resolved.
 */
const runOnTestsReady = (callback) => {
	if (typeof window.hcpExperimentsLoaded != 'undefined' && window.hcpExperimentsLoaded == true) {
		callback();
	} else {
		const handler = () => {
			callback();
			document.removeEventListener('hcpExperimentsLoaded', handler);
		};

		document.addEventListener('hcpExperimentsLoaded', handler);
	}
};

/**
 * Checks if a page-wide A/B test is currently running.
 *
 * This function determines if the global `hcp_ab_test` variable is defined and contains at least one test.
 *
 * @returns {boolean} True if an A/B test is running, false otherwise.
 */
const isPageWideABTestRunning = () => {
	return typeof window.hcp_ab_test != 'undefined' && window.hcp_ab_test.length > 0;
};

/**
 * Generates a random UUID (Universally Unique Identifier).
 *
 * This function first checks if the `crypto.randomUUID` method is available in the browser's `crypto` API.
 * If available, it uses this method to generate a UUID. Otherwise, it falls back to a manual implementation
 * that generates a UUID using `crypto.getRandomValues`.
 *
 * @returns {string} A randomly generated UUID.
 */
const getRandomUUID = () => {
	if (typeof window.crypto.randomUUID !== 'undefined') {
		return crypto.randomUUID();
	}

	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
		(c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
	);
};

/**
 * Checks if a given DOM element is hidden.
 *
 * An element is considered hidden if its `offsetParent` property is `null`.
 *
 * @param {HTMLElement} node - The DOM element to check.
 * @returns {boolean} - Returns `true` if the element is hidden, otherwise `false`.
 */
const elementIsHidden = (node) => {
	return node.offsetParent === null;
};

/**
 * Checks if a given DOM element is visible based on its offset properties.
 *
 * @param {HTMLElement} node - The DOM element to check for visibility.
 * @returns {boolean} - Returns true if the element is visible, otherwise false.
 */
const elementIsVisible = (node) => {
	return Boolean(node.offsetWidth || node.offsetHeight);
};

/**
 * Checks if the current window width is less than or equal to 575 pixels.
 *
 * @returns {boolean} True if the window width is less than or equal to 575 pixels, otherwise false.
 */
const isMobile = () => {
	return window.matchMedia('(max-width: 575px)').matches;
};

/**
 * Checks if the current device is mobile based on the user agent data.
 *
 * @returns {boolean} True if the device is mobile, false otherwise.
 */
const isMobileDevice = () => {
	if (typeof navigator.userAgent === 'undefined') {
		return false;
	}
	return (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
};

/**
 * Checks if the current device is a tablet based on the screen width.
 *
 * @returns {boolean} True if the device is a tablet, false otherwise.
 */
const isTablet = () => {
	return window.matchMedia('(min-width: 576px) and (max-width: 1439px)').matches;
};

/**
 * Checks if the current window width is at least 1440 pixels.
 *
 * @returns {boolean} True if the window width is at least 1440 pixels, otherwise false.
 */
const isDesktop = () => {
	return window.matchMedia('(min-width: 1440px)').matches;
};

/**
 * Waits for a given predicate function to return true.
 *
 * This function repeatedly checks the predicate function at 100ms intervals.
 * Once the predicate function returns true, the interval is cleared and the promise is resolved.
 *
 * @param {Function} predicate - A function that returns a boolean value. The promise resolves when this function returns true.
 * @returns {Promise<void>} A promise that resolves when the predicate function returns true.
 */
function waitFor(predicate, timeout = null) {
	return new Promise((resolve, reject) => {
		const check = () => {
			if (!predicate()) return;
			clearInterval(interval);
			resolve();
		};

		const interval = setInterval(check, 250);
		check();

		if (!timeout) return;
		setTimeout(() => {
			clearInterval(interval);
			reject();
		}, timeout);
	});
}

/**
 * Registers a callback to be executed when a component is ready.
 * If the component is already initialized, the callback is executed immediately.
 * Otherwise, the callback is added to a queue and executed once the component is initialized.
 *
 * @param {string} componentName - The name of the component to wait for.
 * @returns {Function} A function that registers a callback to be executed when the component is ready.
 */
const onComponentReady = (componentName) => (callback) => {
	const initFunctionName = `${componentName}Init`;
	const callbacksArrayName = `${componentName}ReadyCallbacks`;

	if (typeof window[initFunctionName] === 'function') {
		callback();
	} else {
		if (window[callbacksArrayName]) {
			window[callbacksArrayName].push(callback);
		} else {
			window[callbacksArrayName] = [callback];

			waitFor(
				() => {
					return typeof window[initFunctionName] === 'function';
				}, // Wait until the component is available
				10 * 1000 // 10 seconds timeout
			)
				.then(() => {
					for (const cb of window[callbacksArrayName]) {
						cb();
					}
					window[callbacksArrayName].length = 0;
				})
				.catch(() => {
					// Doing it empty to avoid unhandled promise rejection, but if it times out, means that carousel didn't load
				});
		}
	}
};

/**
 * Registers a callback to be executed when the HCPCarousel is ready.
 *
 * @param {Function} callback - The function to be executed when the HCPCarousel is ready.
 */
const onHCPCarouselReady = onComponentReady('HCPCarousel');

/**
 * Registers a callback to be executed when the HCPSelect is ready.
 *
 * @param {Function} callback - The function to be executed when the HCPSelect is ready.
 */
const onHCPSelectReady = onComponentReady('HCPSelect');

/**
 * Executes a callback function when the page becomes visible.
 *
 * @param {Function} callback - The function to execute when the page is visible.
 */
const runWhenPageIsVisible = (callback) => {
	if (elementIsVisible(document.body)) {
		callback();
	} else {
		/**
		 * A MutationObserver instance that observes the visibility of a existing DOM target element.
		 */
		const visibilityObserver = new MutationObserver((mutationsList, observer) => {
			for (const mutation of mutationsList) {
				if (elementIsVisible(mutation.target)) {
					mutation.target.dispatchEvent(new CustomEvent('isVisible'));
					observer.disconnect();
					break;
				}
			}
		});

		const handler = () => {
			callback();
			document.body.removeEventListener('isVisible', handler);
		};

		document.body.addEventListener('isVisible', handler);
		visibilityObserver.observe(document.body, { attributes: true });
	}
};

/**
 * Adds event listeners to a set of elements.
 *
 * @param {string} event - The event type to listen for (e.g., 'click', 'mouseover').
 * @param {string|NodeList} elements - A CSS selector string or a NodeList of elements to which the event listeners will be added.
 * @param {Function} callback - The function to be called when the event is triggered.
 */
const addEventListeners = (event, elements, callback) => {
	if (typeof elements === 'string') {
		elements = document.querySelectorAll(elements);
	}

	if (elements.length) {
		elements.forEach((element) => {
			element.addEventListener(event, callback);
		});
	}
};

/**
 * Executes the provided callback function once the specified Stencil component is ready.
 * Ensure this is called within a runOnDocumentReady callback to guarantee the Stencil component is present in the DOM.
 *
 * @param {Function} callback - The function to execute when the Stencil component is ready.
 * @param {string} [stencilTag='hcp-form'] - The tag name of the Stencil component to wait for. Defaults to 'hcp-form'.
 */
const runOnStencilsReady = (callback, stencilTag = 'hcp-form') => {
	customElements.whenDefined(stencilTag).then(() => {
		const stencilComponent = getElement(document.querySelector(stencilTag));

		if (stencilComponent) {
			stencilComponent.componentOnReady().then(callback);
		}
	});
};

function getDeclaredIndustry() {
	const proData = window.localStorage.getItem('pro_data');
	return proData ? JSON.parse(proData).industry.declared : null;
}

/**
 * Checks if the current user agent is a known web crawler or bot.
 *
 * This function uses a regular expression to test the user agent string against
 * a list of common bot and crawler identifiers including major search engines
 * and web crawlers.
 *
 * @returns {boolean} True if the user agent matches a known bot pattern, false otherwise.
 */
function isBot() {
	// Extend bot detection to include DebugBear user agents - removed debugbear 9-09-2025
	const botRegex = /bot|google|aolbuild|baidu|bing|msn|duckduckgo|teoma|slurp|yandex/i;
	return botRegex.test(navigator.userAgent);
}

/**
 * Executes a callback function when the analytics library is ready.
 * If the analytics library is already loaded and ready, the callback is executed immediately.
 * Otherwise, the callback is queued and executed once the analytics library becomes ready.
 *
 * @param {Function} callback - The function to execute when the analytics library is ready.
 *                              The callback receives the `analytics` object as its argument.
 */
function runOnAnalyticsReady(callback) {
	if (typeof window.analytics !== 'undefined' && typeof window.analytics.track === 'function') {
		callback(window.analytics);
	} else {
		if (!window.analyticsQueue) {
			window.analyticsQueue = [];
		}

		window.analyticsQueue.push(callback);
	}
}

/**
 * Waits for a specific session storage property to be available, with a configurable timeout and fallback.
 *
 * This function polls session storage for a specified property at regular intervals.
 * If the property is found, it executes the callback immediately with the property value.
 * If the property is not found within the timeout period, it executes the callback anyway with null.
 *
 * @param {string} propertyName - The session storage key to wait for.
 * @param {Function} callback - The function to execute when property is found or timeout occurs. Receives the property value as first argument.
 * @param {Object} options - Configuration options.
 * @param {number} [options.timeout=3000] - Maximum time to wait in milliseconds before fallback.
 * @param {number} [options.pollInterval=100] - How often to check for the property in milliseconds.
 */
const waitForSessionStorageProperty = (propertyName, callback, options = {}) => {
	const { timeout = 3000, pollInterval = 100 } = options;
	const startTime = Date.now();

	const checkForProperty = () => {
		const propertyValue = sessionStorage.getItem(propertyName);
		const elapsedTime = Date.now() - startTime;

		if (propertyValue !== null) {
			callback(propertyValue);
			return;
		}

		if (elapsedTime >= timeout) {
			callback(null);
			return;
		}

		setTimeout(checkForProperty, pollInterval);
	};

	checkForProperty();
};

/**
 * Waits for a specific local storage property to be available, with a configurable timeout and fallback.
 *
 * This function polls local storage for a specified property at regular intervals.
 * If the property is found, it executes the callback immediately with the property value.
 * If the property is not found within the timeout period, it executes the callback anyway with null.
 *
 * @param {string} propertyName - The local storage key to wait for.
 * @param {Function} callback - The function to execute when property is found or timeout occurs. Receives the property value as first argument.
 * @param {Object} options - Configuration options.
 * @param {number} [options.timeout=3000] - Maximum time to wait in milliseconds before fallback.
 * @param {number} [options.pollInterval=100] - How often to check for the property in milliseconds.
 */
const waitForLocalStorageProperty = (propertyName, callback, options = {}) => {
	const { timeout = 3000, pollInterval = 100 } = options;
	const startTime = Date.now();

	const checkForProperty = () => {
		const propertyValue = localStorage.getItem(propertyName);
		const elapsedTime = Date.now() - startTime;

		if (propertyValue !== null) {
			callback(propertyValue);
			return;
		}

		if (elapsedTime >= timeout) {
			callback(null);
			return;
		}

		setTimeout(checkForProperty, pollInterval);
	};

	checkForProperty();
};

/**
 * Dispatches a custom event with optional detail data on a specified target.
 *
 * This function creates and dispatches a CustomEvent with the given name and detail object.
 * By default, the event is dispatched on the `window` object, but a different EventTarget
 * can be specified.
 *
 * @param {string} eventName - The name of the custom event to dispatch.
 * @param {Object} [detail={}] - Optional. An object containing data to pass with the event.
 * @param {EventTarget} [target=window] - Optional. The target to dispatch the event on (default: window).
 */
function triggerCustomEvent(eventName, detail = {}, target = window) {
	const event = new CustomEvent(eventName, { detail });
	target.dispatchEvent(event);
}

/**
 * Returns a Promise that resolves with the return value of the callback when the specified custom event is dispatched,
 * or resolves immediately (with the callback's return value) if hasEventFlag is true.
 *
 * @param {string} eventName - The name of the custom event to listen for.
 * @param {Function} callback - The function to execute when the event is dispatched.
 * @param {EventTarget} [target=window] - The target to listen on (default: window).
 * @param {boolean} [hasEventFlag=false] - If true, resolves immediately.
 * @returns {Promise<any>} A promise that resolves with the callback's return value.
 */
function runOnCustomEvent(eventName, callback, target = window, hasEventFlag = false) {
	return new Promise((resolve, reject) => {
		const runCallback = () => {
			try {
				const result = callback();
				if (result && typeof result.then === 'function') {
					// callback returned a promise
					result.then(resolve, reject);
				} else {
					// callback returned a value
					resolve(result);
				}
			} catch (err) {
				reject(err);
			}
		};

		if (hasEventFlag) {
			runCallback();
			return;
		}

		const handler = () => {
			target.removeEventListener(eventName, handler);
			runCallback();
		};

		target.addEventListener(eventName, handler);
	});
}

/**
 * Executes a callback function when the Amplitude SDK is ready.
 *
 * This function listens for the custom event defined by `AMPLITUDE_READY_EVENT`
 * and executes the provided callback when the event is dispatched. If the event
 * has already occurred and a global flag is set (if implemented), the callback
 * may be executed immediately.
 *
 * @param {Function} callback - The function to execute when Amplitude is ready.
 */
function runOnAmplitudeReady(callback) {
	return runOnCustomEvent(AMPLITUDE_READY_EVENT, callback, window, typeof window.variantForExperiment === 'function');
}

/**
 * Waits for the document body to be available before resolving.
 *
 * This function returns a Promise that resolves once `document.body` is present.
 * It uses `requestAnimationFrame` to poll for the body, which is useful for scripts
 * that may run before the DOM is fully constructed.
 *
 * @returns {Promise<void>} A promise that resolves when `document.body` is available.
 */
function waitForBodyToBeReady() {
	return new Promise((resolve) => {
		function checkBody() {
			if (document.body) {
				resolve();
			} else {
				window.requestAnimationFrame(checkBody);
			}
		}

		window.requestAnimationFrame(checkBody);
	});
}

function injectIcons() {
	fetch(ICONS_URL)
		.then((res) => res.text())
		.then((svg) => {
			const div = document.createElement('div');
			div.style.display = 'none';
			div.innerHTML = svg;
			document.body.prepend(div);
		})
		.catch(() => {});
}

/**
 * Gets the variant for a specific experiment, waiting for Amplitude to be ready
 * @param {string} experiment_key - The experiment key
 * @param {number} timeoutInterval - Timeout interval in milliseconds
 * @returns {Promise<string>} Promise that resolves with the experiment variant
 */
function variantForExperiment(experiment_key, timeoutInterval = 1000) {
	return runOnAmplitudeReady(() => {
		return window.variantForExperiment(experiment_key, timeoutInterval);
	});
}

/**
 * Retrieves a stored experiment result from the global cache
 * @param {string} experiment_key - The experiment key
 * @returns {string|null} The stored experiment variant or null if not found
 */
function getStoredExperimentResult(experiment_key) {
	if (!window[EXPERIMENT_RESULTS_GLOBAL_KEY]) {
		return null;
	}

	return window[EXPERIMENT_RESULTS_GLOBAL_KEY][experiment_key] || null;
}

function runWhenExperimentsAreLoaded(callback) {
	if (typeof window.hcpExperimentsLoaded != 'undefined' && window.hcpExperimentsLoaded == true) {
		callback();
	} else {
		const handler = () => {
			callback();
			document.removeEventListener('hcpExperimentsLoaded', handler);
		};

		document.addEventListener('hcpExperimentsLoaded', handler);
	}
}

/**
 * Executes a callback function when the Attribution Identity Graph is available and ready.
 *
 * This function listens for the ATTRIBUTION_READY_EVENT and invokes the provided callback
 * when the event is triggered or immediately if the global window.identityGraph is already present.
 *
 * @param {Function} callback - The function to execute when attribution is set.
 * @returns {*} The result of runOnCustomEvent, typically the return value from the callback if called immediately, or undefined otherwise.
 */
function runWhenAttributionIsSet(callback) {
	return runOnCustomEvent(ATTRIBUTION_READY_EVENT, callback, window, typeof window.identityGraph === 'object');
}

export {
	elementIsHidden,
	getRandomUUID,
	isDesktop,
	isMobile,
	isPageWideABTestRunning,
	isTablet,
	onHCPCarouselReady,
	onHCPSelectReady,
	runOnDocumentReady,
	runOnTestsReady,
	runWhenPageIsVisible,
	addEventListeners,
	runOnStencilsReady,
	getDeclaredIndustry,
	isBot,
	runOnAnalyticsReady,
	waitFor,
	waitForSessionStorageProperty,
	waitForLocalStorageProperty,
	injectIcons,
	triggerCustomEvent,
	runOnCustomEvent,
	runOnAmplitudeReady,
	waitForBodyToBeReady,
	variantForExperiment,
	getStoredExperimentResult,
	runWhenExperimentsAreLoaded,
	runWhenAttributionIsSet,
	isMobileDevice
};
